'use client'

import { useEffect } from 'react'

import { SEGMENT_EVENTS } from '@/configs/dataTrackingEvent'
import useHasAppearedInViewport from '@/hooks/useHasAppearedInViewport'
import { CmsCampaign } from '@/services/coin/cmsCampaign'
import { trackSegmentEvent } from '@/services/dataTracking'
import { toLocaleUrl } from '@/utils/campaign'

import { useCoinContext } from '../../_contexts/CoinContext'
import styles from './index.module.css'

interface CmsCampaignCardProps {
  campaign: CmsCampaign
  locale: string
}

export default function CmsCampaignCard({ campaign, locale }: CmsCampaignCardProps) {
  const { coin } = useCoinContext()
  const { ref, hasAppearedInViewport } = useHasAppearedInViewport()
  const { campaignBanner } = campaign

  useEffect(() => {
    if (hasAppearedInViewport && campaign.id) {
      trackSegmentEvent(SEGMENT_EVENTS.WebPriceCampaignBannerViewed, {
        campaign_id: campaign.id,
        coin: coin.symbol,
        page_path: window.location.pathname,
        locale,
      })
    }
  }, [hasAppearedInViewport])

  if (!campaignBanner?.campaignTitle) return null

  const { campaignTitle, campaignDescription, campaignImage, ctaLabel } = campaignBanner
  const ctaLink = toLocaleUrl(campaignBanner.ctaLink, locale) ?? campaignBanner.ctaLink

  const handleClick = () => {
    trackSegmentEvent(SEGMENT_EVENTS.WebPriceCampaignBannerClicked, {
      campaign_id: campaign.id,
      coin: coin.symbol,
      page_path: window.location.pathname,
      locale,
      destination_url: ctaLink,
    })
  }

  return (
    <div ref={ref} className={styles.card}>
      <div className={styles.imageContainer}>
        <img src={campaignImage.url} alt={campaignImage.alt} className={styles.image} />
        <div className={styles.shadow} />
      </div>
      <div className={styles.content}>
        <p className={styles.title}>{campaignTitle}</p>
        <p className={styles.description}>{campaignDescription}</p>
        <a
          href={ctaLink}
          className={styles.cta}
          onClick={handleClick}
          target="_blank"
          rel="noopener noreferrer"
        >
          {ctaLabel}
        </a>
      </div>
    </div>
  )
}
